import fileDownload from 'js-file-download';
import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbarContext } from 'hooks/useSnackbarContext';
import { getAccessToken } from './jwt';

type DownloadArgs = {
  url: string;
  filename: string;
  requiresAuth?: boolean;
};

class DownloadError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
  }
}

function isEmptyBlob(value: unknown): boolean {
  return value instanceof Blob && value.size === 0;
}

function toOrigin(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

// Origines de nos API authentifiées : tout téléchargement vers l'une d'elles doit porter le Bearer.
const AUTHENTICATED_API_ORIGINS = new Set(
  [
    import.meta.env.VITE_TRANSACTION_SERVICE_URL,
    import.meta.env.VITE_APPLICATIONFORM_SERVICE_URL,
    import.meta.env.VITE_GC_SERVICE_URL,
    import.meta.env.VITE_ADVISOR_REPORT_SERVICE_URL,
    import.meta.env.VITE_DISCOVERY_SERVICE_URL,
    import.meta.env.VITE_DATAREPO_SERVICE_URL,
    import.meta.env.VITE_UFC_SERVICE_URL,
    import.meta.env.VITE_SECURITY_SERVICE_URL,
  ]
    .map(toOrigin)
    .filter((origin): origin is string => origin !== null),
);

export function isProtectedDownloadUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    if (AUTHENTICATED_API_ORIGINS.has(parsed.origin)) return true;
    return parsed.pathname.startsWith('/gc/v1/');
  } catch {
    return url.includes('/gc/v1/');
  }
}

function getDownloadErrorKey(
  error: unknown,
): 'sessionExpired' | 'forbidden' | 'notFound' | 'server' | 'network' | 'generic' | 'emptyFile' {
  if (error instanceof DownloadError) {
    const { status } = error;
    if (status === 401) return 'sessionExpired';
    if (status === 403) return 'forbidden';
    if (status === 404) return 'notFound';
    if (status && status >= 500) return 'server';
    if (!status) return 'network';
    return 'generic';
  }

  if (error instanceof Error && error.message === 'EMPTY_FILE') return 'emptyFile';
  if (error instanceof Error && error.message === 'NO_TOKEN') return 'sessionExpired';

  return 'generic';
}

async function downloadFile({ url, filename, requiresAuth }: DownloadArgs): Promise<void> {
  const shouldSendAuth = requiresAuth ?? isProtectedDownloadUrl(url);

  const headers: Record<string, string> = {};

  if (shouldSendAuth) {
    const accessToken = await getAccessToken();
    if (!accessToken) throw new Error('NO_TOKEN');
    headers.Authorization = `Bearer ${accessToken}`;
  }

  let res: Response;
  try {
    res = await fetch(url, { method: 'GET', headers });
  } catch {
    throw new DownloadError('Network error');
  }

  if (!res.ok) {
    throw new DownloadError(`HTTP ${res.status}`, res.status);
  }

  const blob = await res.blob();

  if (isEmptyBlob(blob)) {
    throw new Error('EMPTY_FILE');
  }

  fileDownload(blob, filename);
}

export default function useDownload() {
  const { t } = useTranslation('downloadError');
  const { showSnackbar } = useSnackbarContext();

  const notifySuccess = useCallback(() => {
    showSnackbar({
      text: t('downloadStartedMessage', { defaultValue: 'Téléchargement démarré.' }),
      severity: 'success',
    });
  }, [showSnackbar, t]);

  const notifyError = useCallback(
    (error: unknown) => {
      const key = getDownloadErrorKey(error);

      const messageByKey: Record<typeof key, string> = {
        sessionExpired: t('downloadErrorSessionExpired', {
          defaultValue: 'Votre session a expiré. Veuillez vous reconnecter.',
        }),
        forbidden: t('downloadErrorForbidden', {
          defaultValue: "Vous n'avez pas les droits pour télécharger ce document.",
        }),
        notFound: t('downloadErrorNotFound', { defaultValue: 'Document introuvable.' }),
        server: t('downloadErrorServer', { defaultValue: 'Erreur serveur. Réessayez plus tard.' }),
        network: t('downloadErrorNetwork', { defaultValue: 'Problème réseau. Vérifiez votre connexion.' }),
        emptyFile: t('downloadErrorEmptyFile', { defaultValue: 'Le fichier est vide ou indisponible.' }),
        generic: t('downloadErrorGeneric', { defaultValue: 'Impossible de télécharger le document.' }),
      };

      showSnackbar({ text: messageByKey[key], severity: 'danger' });
    },
    [showSnackbar, t],
  );

  const mutation = useMutation<void, unknown, DownloadArgs>({
    mutationFn: downloadFile,
    retry: false,
    onSuccess: notifySuccess,
    onError: notifyError,
  });

  return {
    download: (url: string, filename: string, requiresAuth?: boolean) =>
      mutation.mutate({ url, filename, requiresAuth }),

    downloadAsync: (url: string, filename: string, requiresAuth?: boolean) =>
      mutation.mutateAsync({ url, filename, requiresAuth }),

    isDownloading: mutation.isPending,
  };
}
